/**
 * In-tab element clipboard for copy / cut / paste of editable subtrees.
 *
 * Holds at most one snapshot at a time. Cut and paste each commit their own
 * store mutation (two undo steps for cut→paste; one for copy→paste). This
 * matches every other editor's clipboard convention and avoids holding a
 * transaction open across user actions.
 *
 * The snapshot captures:
 *   - htmlFragment: serialized subtree HTML (carries SOURCE eids)
 *   - sourceEids:   ordered list of eids in the fragment (for the eidMap)
 *   - sourceOverridesByEid: snapshot of section.overrides.elements entries
 *                           limited to the sourceEids set
 *   - sourceInstanceId: the section the snapshot came from (used to detect
 *                       same-section pastes — they reuse the existing eid
 *                       allocator behavior)
 *   - adapterKind / itemSelector: used by the paste path to choose the
 *                                 insertion site near the click target
 *   - timestamp:    creation time, for diagnostics only
 *
 * Module-level singleton — the clipboard lives in the browser tab and clears
 * on reload. No IndexedDB persistence.
 */

import { eventBus } from '../core/events.js';

let _clipboard = null;

// The tab-owned clipboard cannot preserve an occurrence after its IDs change.
eventBus.on('editable-ids:repaired', ({ instanceIds = [] } = {}) => {
  if (!instanceIds.includes(_clipboard?.sourceInstanceId)) return;
  clearClipboard();
  eventBus.emit('clipboard:element-changed', { hasClipboard: false, adapterKind: null });
});

/**
 * Take a defensive deep copy of just the overrides entries that map to the
 * captured eids. The full section.overrides may include unrelated entries
 * for elements that aren't in our fragment; those don't need to ride along.
 */
function _snapshotOverridesForEids(section, eids) {
  const out = {};
  if (!section?.overrides?.elements || !Array.isArray(eids)) return out;
  for (const eid of eids) {
    const entry = section.overrides.elements[eid];
    if (entry) out[eid] = JSON.parse(JSON.stringify(entry));
  }
  return out;
}

/**
 * Store a copy snapshot. Replaces any prior clipboard entry. Returns the
 * snapshot for convenience.
 */
export function setClipboardSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return null;
  const {
    htmlFragment,
    sourceEids,
    sourcePrimaryEid = null,
    sourceInstanceId = null,
    sourceOverridesByEid = {},
    adapterKind = null,
    itemSelector = null
  } = snapshot;
  if (typeof htmlFragment !== 'string' || !htmlFragment) return null;
  if (!Array.isArray(sourceEids)) return null;

  _clipboard = {
    kind: 'element',
    htmlFragment,
    sourceEids: sourceEids.slice(),
    sourcePrimaryEid,
    sourceInstanceId,
    sourceOverridesByEid,
    adapterKind,
    itemSelector,
    timestamp: Date.now()
  };
  return _clipboard;
}

/**
 * Convenience: build a snapshot from raw section + eid + serialization output.
 */
export function buildSnapshotFromSerialization({
  section,
  instanceId,
  adapterKind,
  itemSelector,
  htmlFragment,
  sourceEids,
  sourcePrimaryEid
}) {
  return {
    htmlFragment,
    sourceEids,
    sourcePrimaryEid,
    sourceInstanceId: instanceId,
    sourceOverridesByEid: _snapshotOverridesForEids(section, sourceEids),
    adapterKind,
    itemSelector
  };
}

export function getClipboard() {
  return _clipboard;
}

export function hasClipboard() {
  return _clipboard !== null;
}

export function clearClipboard() {
  _clipboard = null;
}

/**
 * Reset internal state. Useful for tests so successive runs don't leak.
 */
export function _resetClipboardForTests() {
  _clipboard = null;
}
